import { Card, CardContent, CardHeader } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { AudienceAvatar, personasQuery, type Persona } from '@/entities/persona';
import { PROJECT_TYPE_MAP, type ProjectDetail } from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { formatShortDate } from '@/shared/utils/format';
import { useTranslation } from 'react-i18next';

/**
 * Resolves the `photoAssetId` for a given audience id by looking it up in the
 * workspace's personas list.
 *
 * The audience entries on the project (`ProjectDetail.audiences`) only carry
 * `id` and `name` — the photo lives on the persona service. We fetch the
 * workspace's personas (cache shared with the Edit Project picker, typically
 * already warm) and build a quick lookup. Missing personas (deleted /
 * unreachable) fall through and the avatar renders the initial fallback.
 */
function buildPersonaIndex(personas: Persona[]): Map<string, Persona> {
  return new Map(personas.map((persona) => [persona.id, persona]));
}

function pickPhotoAssetId(persona: Persona | undefined): string | undefined {
  if (!persona) return undefined;
  // `photoAssetId` is present on both DefaultPersonaDto and ScopedPersonaDto
  // (see `lib/api/personas-client.ts`). Use an `in` guard so we don't have
  // to import the per-variant types here.
  return 'photoAssetId' in persona ? (persona.photoAssetId ?? undefined) : undefined;
}

/**
 * The audience's one-line summary: age band first, then the traits the persona
 * service already derived. Composed here rather than stored, so an audience
 * with partial demographics still renders whatever it does have.
 */
function buildAudienceMeta(persona: Persona | undefined): string | null {
  if (!persona || !('demographicsData' in persona)) return null;
  const demographics = persona.demographicsData;
  if (!demographics) return null;

  const parts = [demographics.data.age, ...demographics.characteristics].filter((part) =>
    part.trim(),
  );

  return parts.length ? parts.join(' · ') : null;
}

interface DetailsSectionProps {
  project: ProjectDetail;
}

interface DetailRowProps {
  label: string;
  value: string | string[] | undefined | null;
}

function DetailRow(props: DetailRowProps & { redesign?: boolean }) {
  const values = Array.isArray(props.value) ? props.value : [props.value];

  return (
    <div className={`flex flex-col ${props.redesign ? 'gap-2xs' : 'gap-1'}`}>
      <span
        className={
          props.redesign
            ? 'text-[13px] leading-6 text-muted-foreground'
            : 'text-sm text-muted-foreground'
        }
      >
        {props.label}
      </span>
      {values.map((val, index) => (
        <span
          key={`${props.label}-${index}`}
          className={
            props.redesign
              ? 'text-[15px] leading-6 font-medium text-foreground'
              : 'text-base font-medium text-foreground'
          }
        >
          {val || '-'}
        </span>
      ))}
    </div>
  );
}

export function DetailsSection({ project }: DetailsSectionProps) {
  const { t } = useTranslation('projects');

  // The project's `audiences` array carries only `{ id, name }`.
  // We hydrate each audience with its persona record so the avatar can
  // resolve a real `photoAssetId` instead of always falling back to the
  // initial-only placeholder. The query is the same one the Edit Project
  // modal uses, so the cache is typically already warm when the user lands
  // on this page from the projects list.
  const workspaceId = useCurrentWorkspaceId();
  const { data: personas = [] } = useQuery(personasQuery(workspaceId));
  const personaIndex = buildPersonaIndex(personas);

  return (
    <Card className="gap-md p-xl shadow-none!" compact>
      {/* Header */}
      <CardHeader className="grid-rows-[auto] items-center gap-0 p-0">
        <div className="flex items-center gap-2">
          <h2 className="type-heading-md leading-[26px] text-foreground">
            {t('projectOverview.detailsSection.details')}
          </h2>
        </div>
        {/* Editing moved into the project header's menu, so the section no
            longer carries its own button. */}
      </CardHeader>

      {/* Content */}
      <CardContent className="flex gap-app-inset p-0">
        {/* Project details item */}
        <div className="flex flex-1 flex-col gap-lg rounded-xl border border-border-light p-xl">
          <span className="text-sm leading-6 font-medium text-foreground-subtle">
            {t('projectOverview.detailsSection.projectDetails')}
          </span>
          {/* The name lives in the page header now, and the design draws
              neither start date nor duration. */}
          <div className="grid grid-cols-2 gap-x-xl gap-y-lg">
            <>
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.type')}
                value={PROJECT_TYPE_MAP[project.type]}
              />
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.market')}
                value={project.market}
              />
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.productLine')}
                value={project.tags.length ? project.tags.map((tag) => tag.name) : null}
              />
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.owner')}
                value={project.creator?.name}
              />
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.launch')}
                value={formatShortDate(project.deadline)}
              />
              <DetailRow
                redesign
                label={t('projectOverview.detailsSection.created')}
                value={formatShortDate(project.createdAt)}
              />
            </>
          </div>
        </div>

        {/* Audiences item */}
        <div className="flex flex-1 flex-col gap-lg rounded-xl border border-border-light p-xl">
          <span className="text-sm leading-6 font-medium text-foreground-subtle">
            {t('projectOverview.detailsSection.audiences')}
          </span>
          <div className="flex flex-col gap-md">
            {project.audiences && project.audiences.length > 0 ? (
              project.audiences.map((audience) => {
                const audienceMeta = buildAudienceMeta(personaIndex.get(audience.id));

                return (
                  <div key={audience.id} className="flex items-center gap-3.5">
                    <AudienceAvatar
                      name={audience.name}
                      photoAssetId={pickPhotoAssetId(personaIndex.get(audience.id))}
                    />
                    <div className="flex flex-col gap-3xs">
                      <span className="text-[15px] leading-6 font-medium text-foreground">
                        {audience.name}
                      </span>
                      {audienceMeta && (
                        <span className="text-sm leading-6 text-muted-foreground">
                          {audienceMeta}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="text-sm text-muted-foreground">
                {t('projectOverview.detailsSection.noAudiencesLinkedToThis')}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
